/**
 * Grading Queue — Figma frame 174:1556 (split view) + 175:874 (empty state).
 *
 *   • Masthead   : "N submissions waiting" + sort pill
 *   • Left rail  : queue list with violet-stripe selection cue
 *   • Right pane : exact submitted source, authoritative rubric,
 *                  criterion scores, feedback, and explicit grade confirmation
 *   • Empty state: typographic "0" hero (frame 175:874) when the queue is empty
 *
 * Backend integration:
 *   - usePendingSubmissions()   → queue list
 *   - useGradeSubmission()      → POST /grading/submissions/:id/grade
 *
 * Rubric criterion IDs and maxima come from the selected lab's stored rubric.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import { useSubmissions, useSubmission, useGradeSubmission } from '../hooks';
import { useToast } from '../components/Toast';
import type { FileItem, GradeInput, RubricItem } from '../types';
import { selectSubmittedFile } from './submittedFiles';
import styles from './Grading.module.css';

interface GradeReview {
  sourceKey: string;
  submissionId: string;
  studentName: string;
  studentEmail: string;
  labTitle: string;
  rubric: RubricItem[];
  data: GradeInput;
}

function daysAgo(iso: string): number {
  const then = new Date(iso).getTime();
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

function waitLabel(iso: string): { text: string; urgent: boolean } {
  const days = daysAgo(iso);
  if (days <= 0) {
    const hrs = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
    return { text: `Waiting ${Math.max(hrs, 1)}h`, urgent: false };
  }
  return {
    text: `Waiting ${days} ${days === 1 ? 'day' : 'days'}`,
    urgent: days >= 4,
  };
}

export const SubmittedFilesViewer: React.FC<{ files: FileItem[] }> = ({
  files,
}) => {
  const [activePath, setActivePath] = useState(files[0]?.path ?? '');
  const activeFile = selectSubmittedFile(files, activePath);

  if (!activeFile) {
    return (
      <div className={styles.viewerEmpty}>
        No submitted source files are available for this submission.
      </div>
    );
  }

  return (
    <>
      <div className={styles.sourceHeader}>
        <div>
          <span className={styles.sourceEyebrow}>Submitted source</span>
          <span className={styles.sourceNote}>
            Exact learner submission · no generated diff
          </span>
        </div>
        <span className={styles.fileCount}>
          {files.length} file{files.length === 1 ? '' : 's'}
        </span>
      </div>
      <nav className={styles.fileTabs} aria-label="Submitted files">
        {files.map((file) => (
          <button
            type="button"
            key={file.path}
            className={`${styles.fileTabButton} ${
              file.path === activeFile.path ? styles.fileTabButtonActive : ''
            }`}
            aria-pressed={file.path === activeFile.path}
            onClick={() => setActivePath(file.path)}
          >
            {file.path}
          </button>
        ))}
      </nav>
      <pre
        className={styles.sourceCode}
        aria-label={`Exact submitted source for ${activeFile.path}`}
      >
        <code>{activeFile.content}</code>
      </pre>
    </>
  );
};

const Grading: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') === 'graded' ? 'graded' : 'pending';
  const userId = searchParams.get('userId') || undefined;
  const labId = searchParams.get('labId') || undefined;
  const rawPage = Number(searchParams.get('page') ?? 1);
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [feedback, setFeedback] = useState('');
  const [review, setReview] = useState<GradeReview | null>(null);
  const [reviewNotice, setReviewNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const reviewHeading = useRef<HTMLHeadingElement>(null);
  const reviewTrigger = useRef<HTMLButtonElement>(null);
  const restoreReviewFocus = useRef(false);

  const {
    data: pendingResp,
    isLoading,
    isError,
    isFetching,
    dataUpdatedAt: submissionsUpdatedAt,
    refetch,
  } = useSubmissions({
    limit: 50,
    page,
    status,
    userId,
    ...(labId ? { labId } : {}),
  });
  const gradeSubmission = useGradeSubmission();
  const toast = useToast();
  const controlsLocked = review !== null || saving || gradeSubmission.isPending;

  const queue = useMemo(() => pendingResp?.data ?? [], [pendingResp?.data]);

  // Auto-select the first item so the viewer is never empty on load.
  useEffect(() => {
    if (saving || gradeSubmission.isPending) return;
    if (!queue.some((item) => item.id === selectedId)) {
      const requested = searchParams.get('submissionId');
      setSelectedId(
        queue.find((item) => item.id === requested)?.id ?? queue[0]?.id ?? null,
      );
    }
  }, [queue, selectedId, searchParams, saving, gradeSubmission.isPending]);

  const changeView = (nextStatus: 'pending' | 'graded', nextPage = 1) => {
    if (controlsLocked) return;
    const next = new URLSearchParams(searchParams);
    next.set('status', nextStatus);
    next.set('page', String(nextPage));
    next.delete('submissionId');
    setSearchParams(next);
    setSelectedId(null);
  };

  const queuedSelection = useMemo(
    () => queue.find((q) => q.id === selectedId) ?? null,
    [queue, selectedId],
  );
  const {
    data: submissionDetail,
    isLoading: isSubmissionLoading,
    isError: submissionFailed,
    isFetching: isSubmissionFetching,
    dataUpdatedAt: submissionUpdatedAt,
    refetch: refetchSubmission,
  } = useSubmission(queuedSelection?.id ?? '');
  const selected = !submissionFailed &&
    submissionDetail?.id === queuedSelection?.id &&
    submissionDetail?.labId === queuedSelection?.labId &&
    submissionDetail?.userId === queuedSelection?.userId
    ? submissionDetail ?? null : null;
  const rubric = selected?.reviewRubric ?? [];
  const hasReviewVersion = /^review-v1:[0-9a-f]{64}$/.test(selected?.reviewVersion ?? '');

  // Whenever the selection changes, reset the rubric scores. If the backend
  // returned an autoScore we bias the default toward it so instructors start
  // near the machine's estimate rather than at zero.
  useEffect(() => {
    if (!selected || rubric.length === 0) {
      setScores({});
      return;
    }
    const bias = selected.autoScore ?? 0;
    setScores(
      Object.fromEntries(
        rubric.map((criterion) => [
          criterion.id,
          Math.min(
            criterion.maxScore,
            Math.round((bias * criterion.maxScore) / 100),
          ),
        ]),
      ),
    );
    setFeedback('');
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = useMemo(
    () => Object.values(scores).reduce((sum, s) => sum + (s || 0), 0),
    [scores],
  );

  // Bind confirmation to the exact draft and the latest successful server
  // responses. A background refresh must never silently authorize a new rubric
  // or a different submission under an already-open confirmation.
  const sourceKey = JSON.stringify({
    selected,
    submissionsUpdatedAt,
    submissionUpdatedAt,
    scores,
    feedback,
    status,
    page,
    userId,
    labId,
  });
  const canReview = Boolean(
    selected &&
      status === 'pending' &&
      selected.status === 'pending' &&
      !isLoading &&
      !isError &&
      !isFetching &&
      !isSubmissionLoading &&
      !submissionFailed &&
      !isSubmissionFetching &&
      hasReviewVersion &&
      rubric.length > 0 &&
      Object.keys(scores).length === rubric.length &&
      rubric.every((criterion) => Number.isFinite(scores[criterion.id])),
  );
  const reviewIsCurrent = review?.sourceKey === sourceKey && canReview;

  useEffect(() => {
    if (review && !saving && !gradeSubmission.isPending && !reviewIsCurrent) {
      setReview(null);
      setReviewNotice(
        'Submission or rubric data changed or could not be verified. Review the current grade again before saving.',
      );
    }
  }, [review, reviewIsCurrent, saving, gradeSubmission.isPending]);

  useEffect(() => {
    if (review) reviewHeading.current?.focus();
    else if (restoreReviewFocus.current) {
      restoreReviewFocus.current = false;
      reviewTrigger.current?.focus();
    }
  }, [review]);

  const handleReview = () => {
    if (!selected || !canReview || controlsLocked || savingRef.current) return;
    setReviewNotice('');
    setReview({
      sourceKey,
      submissionId: selected.id,
      studentName: selected.studentName,
      studentEmail: selected.studentEmail,
      labTitle: selected.labTitle,
      rubric: rubric.map((criterion) => ({ ...criterion })),
      data: {
        expectedReviewVersion: selected.reviewVersion!,
        rubricScores: Object.entries(scores).map(([criteriaId, score]) => ({
          criteriaId,
          score,
        })),
        totalGrade: total,
        feedback,
      },
    });
  };

  const handleGrade = async () => {
    if (
      !review ||
      !reviewIsCurrent ||
      savingRef.current ||
      gradeSubmission.isPending
    )
      return;
    const submittedReview = review;
    savingRef.current = true;
    setSaving(true);
    try {
      await gradeSubmission.mutateAsync({
        submissionId: submittedReview.submissionId,
        data: submittedReview.data,
      });
      toast.showSuccess(`Graded ${submittedReview.studentName}. Nice.`);
      setReview(null);
      setSelectedId(null);
      refetch();
    } catch (error) {
      // A failed write is not approval to retry automatically. Require a fresh
      // review and explicit confirmation before any subsequent attempt.
      setReview(null);
      if ((error as { response?: { status?: number } })?.response?.status === 409) {
        setReviewNotice('The submission or rubric changed on the server. Reload the current evidence and review the grade again.');
        toast.showError('The grading snapshot changed. Review the current submission before saving.');
        void refetchSubmission();
        void refetch();
      } else {
        toast.showError("Couldn't submit that grade — please try again.");
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (!selected || controlsLocked) return;
    const idx = queue.findIndex((q) => q.id === selected.id);
    const next = queue[idx + 1] ?? queue[0];
    setSelectedId(next?.id ?? null);
  };

  const matchingTotal = pendingResp?.pagination.total ?? queue.length;
  const submissionPages = pendingResp && (
    <nav className={styles.sortRow} style={{ flexWrap: 'wrap', maxWidth: '100%' }} aria-label="Submission pages">
      <button
        type="button"
        className={styles.btnGhost}
        disabled={controlsLocked || page <= 1}
        onClick={() => changeView(status, 1)}
      >
        First page
      </button>
      <button
        type="button"
        className={styles.btnGhost}
        disabled={controlsLocked || page <= 1}
        onClick={() => changeView(status, page - 1)}
      >
        Previous
      </button>
      <span aria-live="polite">
        Page {page} of {pendingResp.pagination.totalPages}
      </span>
      <button
        type="button"
        className={styles.btnGhost}
        disabled={controlsLocked || page >= pendingResp.pagination.totalPages}
        onClick={() => changeView(status, page + 1)}
      >
        Next
      </button>
    </nav>
  );

  // ---------- Empty state ----------
  if (isError)
    return (
      <div className={styles.emptyPage} role="alert">
        <h1 className={styles.title}>Couldn&apos;t load submissions</h1>
        <p>The queue is unavailable. No grading state has been inferred.</p>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={() => refetch()}
        >
          Retry
        </button>
      </div>
    );
  if (!isLoading && queue.length === 0) {
    return (
      <div className={styles.emptyPage}>
        <div className={styles.emptyMasthead}>
          <span className={styles.eyebrow}>Grading queue</span>
          <h1 className={styles.title}>
            {matchingTotal > 0 ? 'No submissions on this page'
              : status === 'graded' ? 'No graded submissions' : 'Nothing waiting'}
          </h1>
        </div>
        <div className={styles.emptyCard}>
          {matchingTotal === 0 && (
            <h2 className={styles.emptyZero} aria-hidden="true">0</h2>
          )}
          <div className={styles.emptyAccent} aria-hidden="true" />
          <p className={styles.emptyHead}>
            {matchingTotal > 0
              ? `${matchingTotal} ${status === 'graded' ? 'graded' : 'pending'} submissions still match this view.`
              : status === 'graded'
              ? 'No recorded grades in this view.'
              : 'All caught up.'}
          </p>
          <p className={styles.emptySub}>
            {matchingTotal > 0
              ? 'This page has no results. Choose First page or Previous to return to available submissions.'
              : userId
              ? 'Showing work for the selected student.'
              : 'Showing submissions within your teaching scope.'}
          </p>
          {submissionPages}
          <button
            type="button"
            disabled={controlsLocked}
            onClick={() =>
              changeView(status === 'graded' ? 'pending' : 'graded')
            }
            className={styles.emptyLink}
          >
            {status === 'graded'
              ? 'View pending submissions'
              : 'View graded submissions'}{' '}
            →
          </button>
          {userId && (
            <Link
              to={`/students/${encodeURIComponent(userId)}`}
              className={styles.emptyLink}
            >
              Back to student
            </Link>
          )}
        </div>
        <p className={styles.emptyFooter}>
          {matchingTotal > 0
            ? 'No grade has been inferred from this empty page.'
            : <>Next review window opens when a student submits — you&apos;ll be notified.</>}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* --------------- Masthead --------------- */}
      <header className={styles.masthead}>
        <div className={styles.mastheadCopy}>
          <span className={styles.eyebrow}>Grading queue</span>
          <h1 className={styles.title}>
            {isLoading
              ? 'Loading…'
              : `${pendingResp?.pagination.total ?? queue.length} ${status === 'graded' ? 'graded' : 'pending'} submission${(pendingResp?.pagination.total ?? queue.length) === 1 ? '' : 's'}`}
          </h1>
        </div>
        <div className={styles.sortRow}>
          <label htmlFor="grading-status" className={styles.sortLabel}>
            Show:
          </label>
          <select
            id="grading-status"
            className={styles.sortPill}
            value={status}
            disabled={controlsLocked}
            onChange={(event) =>
              changeView(event.target.value as 'pending' | 'graded')
            }
          >
            <option value="pending">Pending review</option>
            <option value="graded">Graded history</option>
          </select>
          {userId && (
            <Link to={`/students/${encodeURIComponent(userId)}`}>
              Selected student
            </Link>
          )}
        </div>
      </header>

      {/* --------------- Split --------------- */}
      <div className={styles.split}>
        {/* Queue */}
        <aside className={styles.queueCard} aria-label="Queue">
          <header className={styles.queueHead}>
            <h2 className={styles.queueTitle}>Queue</h2>
            <span className={styles.queueCount}>{queue.length}</span>
          </header>
          <ul className={styles.queueList}>
            {queue.map((sub) => {
              const wait = waitLabel(sub.submittedAt);
              const active = sub.id === selectedId;
              return (
                <li
                  key={sub.id}
                  className={`${styles.queueItem} ${active ? styles.queueItemActive : ''}`}
                  onClick={() => {
                    if (!controlsLocked) setSelectedId(sub.id);
                  }}
                  role="button"
                  tabIndex={controlsLocked ? -1 : 0}
                  aria-disabled={controlsLocked}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!controlsLocked) setSelectedId(sub.id);
                    }
                  }}
                  aria-current={active ? 'true' : undefined}
                >
                  <span className={styles.queueName}>{sub.studentName}</span>
                  <span className={styles.queueLab}>{sub.labTitle}</span>
                  <span
                    className={`${styles.queueWait} ${wait.urgent ? styles.queueWaitUrgent : ''}`}
                  >
                    {status === 'graded'
                      ? `Grade: ${sub.finalScore ?? sub.manualScore ?? '—'}`
                      : wait.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Viewer */}
        <section className={styles.viewerCard} aria-label="Submission viewer">
          {submissionFailed ? (
            <div className={styles.viewerEmpty} role="alert">
              <p>Couldn&apos;t verify the selected submission. Grading is disabled.</p>
              <button type="button" className={styles.btnGhost} onClick={() => void refetchSubmission()}>
                Reload submission
              </button>
            </div>
          ) : isSubmissionLoading && queuedSelection ? (
            <div className={styles.viewerEmpty} role="status">Loading the submitted evidence and grading snapshot…</div>
          ) : !selected ? (
            <div className={styles.viewerEmpty}>
              <p>Pick a submission from the queue to start reviewing.</p>
            </div>
          ) : (
            <>
              <header className={styles.viewerHead}>
                <div className={styles.viewerCopy}>
                  <h2 className={styles.viewerTitle}>{selected.labTitle}</h2>
                  <span className={styles.viewerSubtitle}>
                    {selected.studentName} · Submitted{' '}
                    {formatDistanceToNowStrict(new Date(selected.submittedAt), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                <div className={styles.viewerActions}>
                  <button
                    type="button"
                    className={styles.btnGhost}
                    disabled={controlsLocked}
                    onClick={handleSkip}
                  >
                    Skip
                  </button>
                  {status === 'pending' && selected.status === 'pending' && (
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      disabled={controlsLocked || !canReview}
                      ref={reviewTrigger}
                      onClick={handleReview}
                    >
                      Review grade
                    </button>
                  )}
                </div>
              </header>

              {reviewNotice && (
                <p role="status" className={styles.reviewNotice}>
                  {reviewNotice}
                </p>
              )}
              {review && (
                <section
                  className={styles.gradeReview}
                  aria-labelledby="grade-review-heading"
                >
                  <h3 id="grade-review-heading" tabIndex={-1} ref={reviewHeading}>
                    Confirm grade before saving
                  </h3>
                  <p>
                    This saves the following grade and feedback to the
                    learner&apos;s submission.
                  </p>
                  <dl className={styles.reviewIdentity}>
                    <dt>Learner</dt>
                    <dd>
                      {review.studentName}
                      {review.studentEmail && ` · ${review.studentEmail}`}
                    </dd>
                    <dt>Lab</dt>
                    <dd>{review.labTitle}</dd>
                    <dt>Submission</dt>
                    <dd>{review.submissionId}</dd>
                  </dl>
                  <ul
                    className={styles.reviewScores}
                    aria-label="Reviewed criterion scores"
                  >
                    {review.rubric.map((criterion) => (
                      <li key={criterion.id}>
                        <span>
                          {criterion.category}
                          {criterion.description && (
                            <small>{criterion.description}</small>
                          )}
                          {criterion.criteria && Object.entries(criterion.criteria).map(([level, description]) => (
                            <small key={level}>{level}: {description}</small>
                          ))}
                        </span>
                        <strong>
                          {review.data.rubricScores.find(
                            (score) => score.criteriaId === criterion.id,
                          )?.score}{' '}
                          / {criterion.maxScore}
                        </strong>
                      </li>
                    ))}
                  </ul>
                  <p>
                    <strong>Total: {review.data.totalGrade} / 100</strong>
                  </p>
                  <h4>Feedback to save</h4>
                  <p className={styles.reviewFeedback}>
                    {review.data.feedback || 'No feedback entered.'}
                  </p>
                  <div className={styles.reviewActions}>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      disabled={saving || gradeSubmission.isPending}
                      onClick={() => {
                        restoreReviewFocus.current = true;
                        setReview(null);
                      }}
                    >
                      Back to edit
                    </button>
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      disabled={
                        !reviewIsCurrent || saving || gradeSubmission.isPending
                      }
                      onClick={handleGrade}
                    >
                      {saving || gradeSubmission.isPending
                        ? 'Saving…'
                        : 'Confirm and save grade'}
                    </button>
                  </div>
                </section>
              )}

              <div className={styles.viewerBody}>
                {/* Exact submitted source */}
                <div className={styles.sourcePanel}>
                  <SubmittedFilesViewer
                    key={selected.id}
                    files={selected.files ?? []}
                  />
                </div>

                {/* Rubric + grade */}
                {status === 'graded' || selected.status === 'graded' ? (
                  <div className={styles.rubricPanel}>
                    <div className={styles.panelSection}>
                      <h3>Recorded grade</h3>
                      <div className={styles.gradeValue}>
                        {selected.finalScore ?? selected.manualScore ?? '—'} /
                        100
                      </div>
                      <h3>Instructor feedback</h3>
                      <p
                        style={{
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {selected.feedback || 'No feedback recorded.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className={styles.rubricPanel}>
                    <div className={styles.panelSection}>
                      <h3>Rubric</h3>
                      {!hasReviewVersion ? (
                        <p className={styles.rubricState}>
                          A verified grading snapshot is unavailable. Reload the submission before grading.
                        </p>
                      ) : rubric.length === 0 ? (
                        <p className={styles.rubricState}>
                          No authoritative rubric is configured for this lab.
                          Add one before grading.
                        </p>
                      ) : (
                        <ul className={styles.rubricList}>
                          {rubric.map((criterion) => (
                            <li
                              key={criterion.id}
                              className={styles.rubricScoreRow}
                            >
                              <label htmlFor={`rubric-${criterion.id}`}>
                                <span className={styles.rubricCriterion}>
                                  {criterion.category}
                                </span>
                                {criterion.description && (
                                  <span className={styles.rubricDescription}>
                                    {criterion.description}
                                  </span>
                                )}
                                {criterion.criteria && Object.entries(criterion.criteria).map(([level, description]) => (
                                  <span key={level} className={styles.rubricDescription}>{level}: {description}</span>
                                ))}
                              </label>
                              <div className={styles.rubricInputWrap}>
                                <input
                                  id={`rubric-${criterion.id}`}
                                  type="number"
                                  min={0}
                                  max={criterion.maxScore}
                                  step={1}
                                  value={scores[criterion.id] ?? 0}
                                  disabled={controlsLocked}
                                  onChange={(event) => {
                                    const value = Number(event.target.value);
                                    setScores((current) => ({
                                      ...current,
                                      [criterion.id]: Math.min(
                                        criterion.maxScore,
                                        Math.max(
                                          0,
                                          Number.isFinite(value) ? value : 0,
                                        ),
                                      ),
                                    }));
                                  }}
                                  aria-label={`${criterion.category} score`}
                                />
                                <span>/ {criterion.maxScore}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className={styles.panelSection}>
                      <h3>Grade</h3>
                      <div className={styles.gradeValue}>{total} / 100</div>
                    </div>

                    <div className={styles.panelSection}>
                      <h3>Feedback</h3>
                      <textarea
                        aria-label="Feedback for student"
                        maxLength={10000}
                        value={feedback}
                        disabled={controlsLocked}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder="Anchor the feedback in specific lines — what worked, what to change, and why."
                        className={styles.textarea}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
      {submissionPages}
    </div>
  );
};

export default Grading;
